"use client"

import clsx from "clsx"
import { Loader2 } from "lucide-react"
import { memo, useCallback, useMemo, useState, type MouseEvent, type MouseEventHandler } from "react"
import { useDataAttributes } from "../../hooks"
import { BaseInteractiveProps } from "../../utils"
import { TIcon } from "../display/TIcon"
import { TIconNameV2 } from "../display/TIconPathsV2"

/**
 * 아이콘만 있는 정사각형 버튼
 *
 * @description
 * 툴바, 테이블 행 액션, 닫기 버튼 등에 사용하는 아이콘 전용 버튼입니다.
 * TButton과 동일한 color 테마와 비동기 onClick 자동 로딩을 지원합니다.
 *
 * @example
 * ```tsx
 * <TIconButton name="edit" color="ghost" onClick={handleEdit} />
 * <TIconButton name="delete" color="ghost" size="small" onClick={handleDelete} />
 * <TIconButton name="close" color="ghost" onClick={handleClose} />
 * ```
 */
interface TIconButtonProps extends BaseInteractiveProps {
	/** 버튼 DOM 요소 참조. focus(), blur() 등 직접 제어 시 사용 */
	ref?: React.Ref<HTMLButtonElement>
	/**
	 * 버튼 색상 테마 (TButton과 동일한 색상 체계)
	 * @default "primary"
	 * @ai-style "ghost" → 투명 배경, hover 시 연회색 — 가장 자주 사용되는 아이콘 버튼 색상
	 * @ai-style "tertiary" → 밝은 회색 배경 — 비활성 느낌의 아이콘 버튼
	 */
	color?: "primary" | "secondary" | "tertiary" | "line" | "ghost" | "black" | "blurry"
	/**
	 * 버튼 크기 (정사각형)
	 * @default "small"
	 * @ai-style "xsmall" → 24x24px, 아이콘 16px
	 * @ai-style "small" → 32x32px, 아이콘 20px
	 * @ai-style "medium" → 40x40px, 아이콘 20px
	 * @ai-style "large" → 48x48px, 아이콘 24px
	 */
	size?: "xsmall" | "small" | "medium" | "large"
	/** 선택 상태 강조 @default false */
	selected?: boolean
	/** 로딩 스피너 표시 @default false */
	loading?: boolean
	/** 아이콘 이름 (TIconNameV2 타입) — 예: "edit", "delete", "more_vert", "close", "search" */
	name: TIconNameV2
	/** 클릭 핸들러 (Promise 반환 시 자동 로딩 관리) */
	onClick?: (event: MouseEvent<HTMLButtonElement>) => Promise<void> | void
}

const iconSizeMap = {
	xsmall: 16,
	small: 20,
	medium: 20,
	large: 24
} as const

function TIconButtonInner(props: TIconButtonProps) {
	const {
		// HTML 속성
		id,
		className,
		// 스타일
		color = "primary",
		size = "small",
		// 상태
		selected,
		disabled,
		loading,
		// 아이콘
		name,
		// 데이터
		dataAttributes,
		// 핸들러
		onClick,
		// React 19 ref prop
		ref,
		"aria-label": ariaLabel,
		...restProps
	} = props

	const [processing, setProcessing] = useState<boolean>(false)
	const isLock = processing || loading
	const dataProps = useDataAttributes(dataAttributes)

	const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			if (disabled || loading || processing) {
				event.preventDefault()
				return
			}
			const result = onClick?.(event)
			if (result instanceof Promise) {
				setProcessing(true)
				result.finally(() => {
					setProcessing(false)
				})
			}
		},
		[disabled, loading, processing, onClick]
	)

	const iconSize = useMemo(() => iconSizeMap[size], [size])

	return (
		<button
			{...restProps}
			ref={ref}
			type="button"
			id={id}
			className={clsx("TIconButton", color, size, className)}
			disabled={disabled}
			aria-label={ariaLabel ?? `${name} button`}
			aria-pressed={selected}
			data-loading={isLock}
			data-selected={selected}
			{...dataProps}
			onClick={handleClick}
		>
			{isLock ? (
				<span className="loader">
					<Loader2 aria-hidden="true" style={{ width: iconSize, height: iconSize }} />
				</span>
			) : null}
			<span className={clsx("icon", { invisible: isLock })}>
				<TIcon name={name} size={iconSize} />
			</span>
		</button>
	)
}

TIconButtonInner.displayName = "TIconButton"

export const TIconButton = memo(TIconButtonInner)
